import { Component, createRef } from "react"

/* Components */
import { DataLayout, Pagination, Icon, Avatar } from "../../components"

/* Widgets */
import { ServiceModal, CategoryPicker } from "../../widgets/service"
import { toast, confirm } from "../../widgets/common"

/* Shared */
import { SORTS, STATUSES, duration, money, priceText } from "../../widgets/service/shared"
import { organizationIcon } from "../../widgets/organization/shared"
import { plural, toneOf } from "../../widgets/category/shared"

/* Constants */
import { env } from "../../constants"

/* REST API */
import { list, save, bulk, applySuggestions } from "../../api/Service"


/* Page Services — the services of every point of the platform. The main work here is to file each
   of them into a category, so the page shows what is not filed yet, suggests where it belongs and
   lets many services be filed at once */
class Services extends Component {

    constructor() {
        super()

        this.state = {
            status: "loading",
            data: [],
            count: 0,
            limit: 30,
            page: 1,
            statistics: null,
            categories: [],

            /* The filters — "status" is all | none | archived, "category" is "all" or an id */
            search: "",
            filter: "all",
            category: "all",
            organization: null,
            sort: "new",

            /* The ids of the chosen services — they survive the pages, not the filters */
            selected: [],

            /* The id of a line whose category is being saved, "bulk" for the bar, "all" for the banner */
            waiting: null
        }

        this._service = createRef()
        this._picker = createRef()
        this._timer = null
    }

    componentDidMount = () => {

        window.scrollTo(0, 0)
        this.load(1, true)
    }

    componentWillUnmount = () => {
        clearTimeout(this._timer)
    }

    /* Load action */
    load = (page = 1, reload = false) => {

        /* Fields */
        const { search, filter, category, organization, sort } = this.state

        if (reload) {
            this.setState({ status: "loading" })
        }

        this.setState({ page })

        /* The category of the filter and "Без категории" are one field of the server */
        let categoryID = category === "all" ? null : category

        if (filter === "none") {
            categoryID = "none"
        }

        list({
            page,
            search,
            categoryID,
            status: filter === "archived" ? "archived" : "all",
            organizationID: organization ? organization.id : null,
            sort
        }).then(response => {
            if (response && response.status === 200) {
                this.setState({
                    status: "ok",
                    data: response.data.list,
                    count: response.data.count,
                    limit: response.data.limit,
                    statistics: response.data.statistics,
                    categories: response.data.categories
                })
            }
            else {
                this.setState({ status: "error", data: [], count: 0 })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [], count: 0 })
        })
    }

    /* Every filter starts from the first page and forgets the chosen lines */
    refilter = fields => {
        this.setState({ ...fields, selected: [] }, () => this.load(1, true))
    }

    /* The search waits for the hand to stop */
    searchText = text => {

        clearTimeout(this._timer)
        this.setState({ search: text })

        this._timer = setTimeout(() => this.refilter({}), text.length === 0 ? 0 : 450)
    }

    /* Choose a line or let it go */
    select = (event, id) => {

        event.stopPropagation()

        const { selected } = this.state

        if (selected.includes(id)) {
            this.setState({ selected: selected.filter(item => item !== id) })
        }
        else {
            this.setState({ selected: [...selected, id] })
        }
    }

    /* Choose the whole page or let it go */
    selectPage = () => {

        const { data, selected } = this.state
        const ids = data.map(item => item.id)
        const all = ids.every(id => selected.includes(id))

        if (all) {
            this.setState({ selected: selected.filter(id => !ids.includes(id)) })
        }
        else {
            this.setState({ selected: [...new Set([...selected, ...ids])] })
        }
    }

    /* Put a category on one line right from the list */
    setCategory = (item, categoryID) => {

        const value = categoryID === "none" ? null : categoryID

        if (parseInt(value || 0) === parseInt(item.categoryID || 0)) {
            return
        }

        this.setState({ waiting: item.id })

        save({ id: item.id, categoryID: value }).then(response => {

            this.setState({ waiting: null })

            if (response && response.status === 200) {

                const category = this.state.categories.find(element => parseInt(element.id) === parseInt(value))
                const data = this.state.data.map(element => element.id === item.id ? { ...element, categoryID: value, category: category ? category.name : null, suggestion: value ? null : element.suggestion } : element)

                this.setState({ data }, () => this.refresh())

                toast.success(category ? `«${item.name.trim()}» → ${category.name}` : "Категория снята")
            }
            else {
                toast.error("Не удалось сохранить категорию")
            }
        }).catch(() => {
            this.setState({ waiting: null })
            toast.error("Не удалось сохранить категорию")
        })
    }

    /* The numbers of the page follow a change without redrawing the list under the hand */
    refresh = () => {
        this.load(this.state.page)
    }

    /* Open the category of a line */
    pick = (event, item) => {

        event.stopPropagation()

        this._picker.current.open(event.currentTarget, {
            selected: item.categoryID ? parseInt(item.categoryID) : null,
            none: Boolean(item.categoryID),
            title: item.name.trim(),
            onSelect: value => this.setCategory(item, value)
        })
    }

    /* Take the suggestion of one line */
    accept = (event, item) => {

        event.stopPropagation()

        if (item.suggestion) {
            this.setCategory(item, item.suggestion.categoryID)
        }
    }

    /* Take every suggestion of the platform */
    acceptAll = () => {

        const { statistics } = this.state

        confirm({
            title: `Разложить ${statistics.suggestions} ${plural(statistics.suggestions, "услугу", "услуги", "услуг")}?`,
            text: "Каждая получит категорию из подсказки. Услуги, которым уже выбрали категорию, не изменятся",
            confirmText: "Да, разложить",
            cancelText: "Нет"
        }).then(confirmed => {

            if (!confirmed) {
                return
            }

            this.setState({ waiting: "all" })

            applySuggestions({}).then(response => {

                this.setState({ waiting: null })

                if (response && response.status === 200) {
                    toast.success(`Категория выбрана для ${response.data.updated} ${plural(response.data.updated, "услуги", "услуг", "услуг")}`)
                    this.load(1, true)
                }
                else {
                    toast.error("Что-то пошло не так! Попробуйте позднее")
                }
            }).catch(() => {
                this.setState({ waiting: null })
                toast.error("Что-то пошло не так! Попробуйте позднее")
            })
        })
    }

    /* One change for every chosen line */
    bulk = (fields, success) => {

        const { selected } = this.state

        this.setState({ waiting: "bulk" })

        bulk({ ids: selected, ...fields }).then(response => {

            this.setState({ waiting: null })

            if (response && response.status === 200) {
                toast.success(success)
                this.setState({ selected: [] }, () => this.load(this.state.page))
            }
            else {
                toast.error("Что-то пошло не так! Попробуйте позднее")
            }
        }).catch(() => {
            this.setState({ waiting: null })
            toast.error("Что-то пошло не так! Попробуйте позднее")
        })
    }

    /* The category for every chosen line */
    bulkCategory = event => {

        const { selected, categories } = this.state

        this._picker.current.open(event.currentTarget, {
            none: true,
            title: `${selected.length} ${plural(selected.length, "услуга", "услуги", "услуг")}`,
            onSelect: value => {
                const category = categories.find(element => parseInt(element.id) === parseInt(value))
                this.bulk({ categoryID: value === "none" ? null : value }, category ? `Категория «${category.name}» выбрана` : "Категория снята")
            }
        })
    }

    /* The category of the filter */
    pickFilter = event => {

        const { category } = this.state

        this._picker.current.open(event.currentTarget, {
            selected: category,
            all: true,
            title: "Показать категорию",
            onSelect: value => this.refilter({ category: value, filter: this.state.filter === "none" ? "all" : this.state.filter })
        })
    }

    /* Draw Header */
    _header = () => {

        /* Fields */
        const { search } = this.state

        return (
            <div className="organization-header">

                <div className="page-header-text">Услуги</div>

                <div className="organization-header-tools">

                    <form onSubmit={event => { event.preventDefault(); clearTimeout(this._timer); this.refilter({}) }} className="organization-search service-search">

                        <div className="organization-search-icon">
                            <Icon name="search" size={17} />
                        </div>

                        <input placeholder="Услуга или точка" value={search} onChange={event => this.searchText(event.target.value)} />

                        {search.length > 0 &&
                            <div onClick={() => this.searchText("")} className="organization-search-clear">
                                <Icon name="close" size={11} width={2.4} />
                            </div>
                        }

                    </form>

                </div>

            </div>
        )
    }

    /* The numbers of the platform */
    _summary = () => {

        /* Fields */
        const { statistics, filter } = this.state

        if (!statistics) {
            return null
        }

        const filed = statistics.total > 0 ? Math.round((1 - statistics.uncategorized / statistics.total) * 100) : 100

        return (
            <div className="service-summary">

                <div className="service-summary-main">
                    <div className="service-summary-ring" style={{ "--value": filed }}>
                        <span>{filed}%</span>
                    </div>
                    <div className="service-summary-main-text">
                        <b>{statistics.total.toLocaleString("ru-RU")} {plural(statistics.total, "услуга", "услуги", "услуг")}</b>
                        <span>разложены по категориям на {filed}% · {statistics.organizations} {plural(statistics.organizations, "точка", "точки", "точек")}</span>
                    </div>
                </div>

                <div onClick={() => this.refilter({ filter: filter === "none" ? "all" : "none", category: "all" })} className={`service-summary-item orange ${filter === "none" ? "active" : ""}`}>
                    <div className="category-summary-icon"><Icon name="alert" size={18} width={2} /></div>
                    <div className="category-summary-text">
                        <b>{statistics.uncategorized}</b>
                        <span>без категории</span>
                    </div>
                </div>

                <div onClick={() => this.refilter({ filter: filter === "archived" ? "all" : "archived" })} className={`service-summary-item grey ${filter === "archived" ? "active" : ""}`}>
                    <div className="category-summary-icon"><Icon name="archive" size={18} width={2} /></div>
                    <div className="category-summary-text">
                        <b>{statistics.archived}</b>
                        <span>в архиве</span>
                    </div>
                </div>

            </div>
        )
    }

    /* The banner of the suggestions */
    _banner = () => {

        /* Fields */
        const { statistics, waiting } = this.state

        if (!statistics || statistics.suggestions === 0) {
            return null
        }

        return (
            <div className="service-banner">
                <div className="service-banner-icon">
                    <Icon name="sparkle" size={22} width={1.7} />
                </div>
                <div className="service-banner-text">
                    <b>Подсказки для {statistics.suggestions} {plural(statistics.suggestions, "услуги", "услуг", "услуг")} без категории</b>
                    <span>Категория подобрана по таким же услугам других точек и по названию. Проверьте и разложите одним нажатием.</span>
                </div>
                <div onClick={() => waiting !== "all" && this.acceptAll()} className="service-banner-button">
                    {waiting === "all" ? "Раскладываем..." : "Разложить все"}
                </div>
            </div>
        )
    }

    /* The filters over the list */
    _toolbar = () => {

        /* Fields */
        const { filter, category, organization, sort, categories, statistics } = this.state

        const chosen = category !== "all" ? categories.find(item => parseInt(item.id) === parseInt(category)) : null

        return (
            <div className="service-toolbar">

                <div className="service-segment">
                    {STATUSES.map(item =>
                        <div key={item.key} onClick={() => this.refilter({ filter: item.key, category: item.key === "none" ? "all" : category })} className={filter === item.key ? "active" : ""}>
                            {item.text}
                            {item.key === "none" && statistics && statistics.uncategorized > 0 && <span>{statistics.uncategorized}</span>}
                        </div>
                    )}
                </div>

                <div onClick={this.pickFilter} className={`service-filter ${chosen ? "active" : ""}`}>
                    <Icon name="category" size={15} width={2} />
                    {chosen ? chosen.name : "Все категории"}
                    {chosen
                        ? <div onClick={event => { event.stopPropagation(); this.refilter({ category: "all" }) }} className="service-filter-clear"><Icon name="close" size={9} width={2.6} /></div>
                        : <Icon name="down" size={14} width={2.2} />
                    }
                </div>

                {organization &&
                    <div className="service-filter active">
                        <Icon name="organization" size={15} width={2} />
                        {organization.name}
                        <div onClick={() => this.refilter({ organization: null })} className="service-filter-clear"><Icon name="close" size={9} width={2.6} /></div>
                    </div>
                }

                <div className="service-sort">
                    <Icon name="sort" size={15} width={1.9} />
                    {SORTS.map(item =>
                        <div key={item.key} onClick={() => this.refilter({ sort: item.key })} className={sort === item.key ? "active" : ""}>{item.text}</div>
                    )}
                </div>

            </div>
        )
    }

    /* The category cell of a line — the category, or the choice with the suggestion */
    _category = item => {

        /* Fields */
        const { waiting, categories } = this.state

        if (waiting === item.id) {
            return <div className="service-pill loading"><Icon name="refresh" size={13} width={2} />Сохраняем</div>
        }

        if (item.categoryID) {

            const category = categories.find(element => parseInt(element.id) === parseInt(item.categoryID))
            const tone = toneOf(category ? category.typeID : 0)

            return (
                <div onClick={event => this.pick(event, item)} className="service-pill" style={{ "--tone": tone.accent, "--tone-soft": tone.soft }} title="Сменить категорию">
                    <i />
                    <span>{item.category ? item.category : "Категория"}</span>
                    <Icon name="down" size={12} width={2.4} />
                </div>
            )
        }

        return (
            <div className="service-category-empty">

                <div onClick={event => this.pick(event, item)} className="service-pill empty">
                    <Icon name="plus" size={12} width={2.4} />
                    <span>Выбрать</span>
                </div>

                {item.suggestion &&
                    <div onClick={event => this.accept(event, item)} className="service-suggestion" title={item.suggestion.source === "twins" ? `Так её разложили ${item.suggestion.count} ${plural(item.suggestion.count, "раз", "раза", "раз")} в других точках` : "По названию услуги"}>
                        <Icon name="sparkle" size={13} width={1.8} />
                        <span>{item.suggestion.name}</span>
                        <em><Icon name="check" size={11} width={3} /></em>
                    </div>
                }

            </div>
        )
    }

    /* One line of the list */
    _item = item => {

        /* Fields */
        const { selected } = this.state

        const chosen = selected.includes(item.id)
        const archived = parseInt(item.active) === 0
        const image = item.organizationImage ? `${env.mediapoint}organizations/${item.organizationImage}` : null
        const discounted = item.discount && parseInt(item.discount) < parseInt(item.price)

        return (
            <div key={`${item.id}`} onClick={() => this._service.current.open(item)} className={`service-item ${chosen ? "chosen" : ""} ${archived ? "archived" : ""}`}>

                <div className="service-item-cell check">
                    <div onClick={event => this.select(event, item.id)} className={`service-check ${chosen ? "on" : ""}`}>
                        <Icon name="check" size={12} width={3} />
                    </div>
                </div>

                <div className="service-item-cell name">
                    <div className="service-item-dot" style={{ background: item.color ? item.color : "#b0bec5" }} />
                    <div className="service-item-title">
                        <div className="service-item-name">
                            {`${item.name}`.trim()}
                            {archived && <div className="service-item-badge">Архив</div>}
                        </div>
                        <div className="service-item-sub">
                            <Icon name="clock" size={12} width={2} />
                            {duration(item.sessionTime)}
                            {item.bookings > 0 && <><b>·</b>{item.bookings} {plural(item.bookings, "запись", "записи", "записей")}</>}
                        </div>
                    </div>
                </div>

                <div className="service-item-cell point">
                    <div onClick={event => { event.stopPropagation(); this.refilter({ organization: { id: item.organizationID, name: item.organization } }) }} className="service-item-point" title="Все услуги точки">
                        <Avatar className="service-item-avatar" src={image} alt={item.organization}>
                            {organizationIcon()}
                        </Avatar>
                        <span>{item.organization}</span>
                    </div>
                </div>

                <div className="service-item-cell price">
                    {discounted
                        ? <div className="service-item-price">
                            <b>{money(item.discount)}</b>
                            <s>{priceText(item)}</s>
                        </div>
                        : <div className="service-item-price"><b>{priceText(item)}</b></div>
                    }
                </div>

                <div className="service-item-cell category">
                    {this._category(item)}
                </div>

            </div>
        )
    }

    /* Draw Content */
    _content = () => {

        /* Fields */
        const { status, data, count, page, limit, selected } = this.state

        if (status !== "ok" || data.length === 0) {
            return (
                <div className="page-content">
                    {this._summary()}
                    {this._banner()}
                    {this._toolbar()}
                    <DataLayout status={status} reload={() => this.load(1, true)} data={data} onReset={() => this.refilter({ search: "", filter: "all", category: "all", organization: null })} />
                </div>
            )
        }

        const all = data.every(item => selected.includes(item.id))

        return (
            <div className="page-content">

                {this._summary()}
                {this._banner()}
                {this._toolbar()}

                <div className="service-list">

                    <div className="service-item head">
                        <div className="service-item-cell check">
                            <div onClick={this.selectPage} className={`service-check ${all ? "on" : ""}`} title="Выбрать страницу">
                                <Icon name="check" size={12} width={3} />
                            </div>
                        </div>
                        <div className="service-item-cell name">Услуга <em>{count.toLocaleString("ru-RU")}</em></div>
                        <div className="service-item-cell point">Точка</div>
                        <div className="service-item-cell price">Цена</div>
                        <div className="service-item-cell category">Категория</div>
                    </div>

                    {data.map(item => this._item(item))}

                </div>

                <Pagination size={limit} page={page} total={count} onPageChange={page => { window.scrollTo(0, 0); this.load(page, true) }} />

            </div>
        )
    }

    /* The bar of the chosen lines */
    _bar = () => {

        /* Fields */
        const { selected, waiting } = this.state

        return (
            <div className={`service-bar ${selected.length > 0 ? "visible" : ""}`}>

                <div className="service-bar-count">
                    <b>{selected.length}</b>
                    <span>{plural(selected.length, "выбрана", "выбраны", "выбрано")}</span>
                </div>

                <div onClick={event => waiting !== "bulk" && this.bulkCategory(event)} className="service-bar-button primary">
                    <Icon name="category" size={16} width={2} />
                    Категория
                </div>

                <div onClick={() => waiting !== "bulk" && this.bulk({ active: 0 }, "Услуги убраны в архив")} className="service-bar-button">
                    <Icon name="archive" size={16} width={2} />
                    В архив
                </div>

                <div onClick={() => waiting !== "bulk" && this.bulk({ active: 1 }, "Услуги вернулись в прайс-лист")} className="service-bar-button">
                    <Icon name="refresh" size={16} width={2} />
                    Вернуть
                </div>

                <div onClick={() => this.setState({ selected: [] })} className="service-bar-close" title="Снять выбор">
                    <Icon name="close" size={11} width={2.4} />
                </div>

            </div>
        )
    }

    render = () => {

        return (
            <div className="page service-page">

                {this._header()}

                {this._content()}

                {this._bar()}

                <ServiceModal ref={this._service} categories={this.state.categories} onSaved={() => this.load(this.state.page)} />

                <CategoryPicker ref={this._picker} categories={this.state.categories} />

            </div>
        )
    }
}

export default Services
